import { stringify } from 'yaml';
import {
  AUTO_GROUP_NAME,
  FINAL_GROUP_NAME,
  GROUPS,
  INFO_NODE_PATTERN,
  ITEM_BY_ID,
  MIRRORS,
  PROXY_GROUP_NAME,
  REGIONS,
  type GroupKind,
  type RegionDef,
  type RuleItem,
  type RuleSetRef,
} from './catalog.ts';
import { targetOf, type Outbound, type Upstream, type UserConfig } from './config.ts';
import { CUSTOM_BY_ID, customDomains, effectiveOrder } from './order.ts';

/** 与 cfg.upstreams 一一对应 */
export type ProxySource = { kind: 'inline'; proxies: Record<string, unknown>[] } | { kind: 'provider' };

export interface GenerateOptions {
  /** 用于「本站缓存代理」镜像,例如 https://sub.example.com */
  origin: string;
  /** 本站规则代理的访问密钥 */
  rulesKey?: string;
}

export interface ServiceGroup {
  /** 分类组 key,或 own:<规则 id> */
  id: string;
  name: string;
  kind: GroupKind;
}

const TEST_URL = 'https://www.gstatic.com/generate_204';
const INFO_RE = new RegExp(INFO_NODE_PATTERN, 'iu');
const EXCLUDE_INFO = `(?i)${INFO_NODE_PATTERN}`;

export function providerName(ref: RuleSetRef): string {
  const n = ref.name.replace(/!/g, 'not-').replace(/@/g, '-at-');
  return `${ref.type === 'geosite' ? 'site' : 'ip'}-${n}`;
}

export function ruleSetUrl(ref: RuleSetRef, mirror: UserConfig['mirror'], origin: string, rulesKey?: string): string {
  const base = mirror === 'self' ? `${origin}/rules` : MIRRORS.find((m) => m.key === mirror)!.base;
  // fastly.jsdelivr.net 对未编码的 ! 返回 403,需显式转义
  const url = `${base}/${ref.type}/${encodeURIComponent(ref.name).replace(/!/g, '%21')}.mrs`;
  return mirror === 'self' ? `${url}?k=${encodeURIComponent(rulesKey ?? '')}` : url;
}

const groupDef = (key: RuleItem['group']) => GROUPS.find((g) => g.key === key)!;

export function ownGroupName(item: RuleItem): string {
  return `${groupDef(item.group).name.split(' ')[0]} ${item.label}`;
}

export function regionAutoName(r: RegionDef): string {
  return `${r.name} 自动`;
}

export function airportGroupName(u: Upstream): string {
  return `✈️ ${u.name}`;
}

function checkedItems(cfg: UserConfig): RuleItem[] {
  return cfg.rules
    .map((id) => ITEM_BY_ID.get(id))
    .filter((i): i is RuleItem => !!i)
    .sort((a, b) => a.order - b.order);
}

/** 根据规则去向推导需要生成的服务策略组;没有规则指向的组不生成 */
export function planServiceGroups(cfg: UserConfig): ServiceGroup[] {
  const items = checkedItems(cfg);
  const out: ServiceGroup[] = [];
  for (const g of GROUPS) {
    const inGroup = items.filter((i) => i.group === g.key);
    if (g.key !== 'proxy' && inGroup.some((i) => targetOf(cfg, i) === 'cat')) out.push({ id: g.key, name: g.name, kind: g.kind });
    for (const i of inGroup) if (targetOf(cfg, i) === 'own') out.push({ id: `own:${i.id}`, name: ownGroupName(i), kind: g.kind });
  }
  return out;
}

interface Node {
  name: string;
  /** 加机场前缀之前的名字,用于地区匹配 */
  orig: string;
  air: number;
}

export function generateConfig(cfg: UserConfig, sources: ProxySource[], opts: GenerateOptions): Record<string, unknown> {
  const multi = cfg.upstreams.length > 1;
  const services = planServiceGroups(cfg);

  const reserved = new Set([
    PROXY_GROUP_NAME,
    AUTO_GROUP_NAME,
    FINAL_GROUP_NAME,
    'DIRECT',
    'REJECT',
    ...services.map((s) => s.name),
    ...REGIONS.flatMap((r) => [r.name, regionAutoName(r)]),
    ...cfg.upstreams.map(airportGroupName),
  ]);

  // ---- 节点来源:内联节点 + proxy-providers ----
  const proxies: Record<string, unknown>[] = [];
  const nodes: Node[] = [];
  const proxyProviders: Record<string, unknown> = {};
  const providerOf: (string | undefined)[] = [];
  const seen = new Set(reserved);

  cfg.upstreams.forEach((up, i) => {
    const src = sources[i] ?? { kind: 'provider' };
    if (src.kind === 'inline') {
      for (const p of src.proxies) {
        if (!p || typeof p.name !== 'string' || typeof p.type !== 'string') continue;
        const base = multi ? `${up.name} | ${p.name}` : p.name;
        let name = base;
        for (let n = 2; seen.has(name); n++) name = `${base} ${n}`;
        seen.add(name);
        proxies.push({ ...p, name });
        if (!INFO_RE.test(p.name)) nodes.push({ name, orig: p.name, air: i });
      }
    } else {
      const pname = `airport-${i + 1}`;
      providerOf[i] = pname;
      proxyProviders[pname] = {
        type: 'http',
        url: up.url,
        interval: 3600,
        proxy: 'DIRECT',
        header: { 'User-Agent': [cfg.ua] },
        'health-check': { enable: true, url: TEST_URL, interval: 600, lazy: true },
        ...(multi ? { override: { 'additional-prefix': `${up.name} | ` } } : {}),
      };
    }
  });
  const providers = providerOf.filter((p): p is string => !!p);
  const nodeNames = nodes.map((n) => n.name);

  /** 同时引用内联节点与 provider 的组成员 */
  const members = (names: string[], use: string[], filter?: string): Record<string, unknown> => ({
    ...(names.length ? { proxies: names } : {}),
    ...(use.length ? { use, ...(filter ? { filter } : {}), 'exclude-filter': EXCLUDE_INFO } : {}),
  });

  // ---- 地区组:内联节点在这里就按原始名字归类;provider 节点交给客户端 filter ----
  const regionGroups = REGIONS.filter((r) => cfg.regions.includes(r.key))
    .map((r) => {
      const re = new RegExp(r.pattern, 'iu');
      return { def: r, names: nodes.filter((n) => re.test(n.orig)).map((n) => n.name) };
    })
    .filter((r) => r.names.length > 0 || providers.length > 0);

  const airportGroups = multi
    ? cfg.upstreams
        .map((up, i) => ({ i, name: airportGroupName(up), names: nodes.filter((n) => n.air === i).map((n) => n.name), use: providerOf[i] ? [providerOf[i]!] : [] }))
        .filter((a) => a.names.length > 0 || a.use.length > 0)
    : [];

  // both:每个地区同时生成手动选择组和自动测速组
  const both = cfg.regionType === 'both';
  const regionNames = regionGroups.flatMap((r) => (both ? [r.def.name, regionAutoName(r.def)] : [r.def.name]));
  const airportNames = airportGroups.map((a) => a.name);

  const outName = (o: Outbound): string | undefined => {
    if (o === 'proxy') return PROXY_GROUP_NAME;
    if (o === 'auto') return AUTO_GROUP_NAME;
    if (o === 'direct') return 'DIRECT';
    if (o === 'reject') return 'REJECT';
    if (o.startsWith('air:')) return airportGroups.find((a) => a.i === Number(o.slice(4)))?.name;
    const isAuto = o.endsWith(':auto');
    const r = regionGroups.find((g) => g.def.key === (isAuto ? o.slice(0, -5) : o));
    if (!r) return undefined;
    return isAuto && both ? regionAutoName(r.def) : r.def.name;
  };

  const serviceOptions = (s: ServiceGroup): string[] => {
    const base =
      s.kind === 'reject'
        ? ['REJECT', 'DIRECT']
        : s.kind === 'direct'
          ? ['DIRECT', PROXY_GROUP_NAME, ...regionNames, ...airportNames]
          : [PROXY_GROUP_NAME, AUTO_GROUP_NAME, ...regionNames, ...airportNames, 'DIRECT', 'REJECT'];
    const pref = cfg.groupDefaults[s.id];
    const first = pref ? outName(pref) : undefined;
    return first ? [first, ...base.filter((n) => n !== first)] : base;
  };

  /** 开启 flatNodes 时在分组选项之后平铺所有节点,节点少、没有地区概念时也能直接点选 */
  const flatNodes = cfg.flatNodes && providers.length ? { use: providers, 'exclude-filter': EXCLUDE_INFO } : {};
  const withNodes = (opts: string[]) => (cfg.flatNodes ? [...opts, ...nodeNames] : opts);

  const urlTest = { url: TEST_URL, interval: 300, tolerance: 50, lazy: true };
  const autoMembers = members(nodeNames, providers);
  const proxyGroups: Record<string, unknown>[] = [
    { name: PROXY_GROUP_NAME, type: 'select', proxies: [AUTO_GROUP_NAME, ...regionNames, ...airportNames, 'DIRECT', ...nodeNames], ...(providers.length ? { use: providers, 'exclude-filter': EXCLUDE_INFO } : {}) },
    { name: AUTO_GROUP_NAME, type: 'url-test', ...urlTest, ...(Object.keys(autoMembers).length ? autoMembers : { proxies: ['DIRECT'] }) },
    ...services.map((s) =>
      s.kind === 'reject'
        ? { name: s.name, type: 'select', proxies: serviceOptions(s) }
        : { name: s.name, type: 'select', proxies: withNodes(serviceOptions(s)), ...flatNodes },
    ),
    { name: FINAL_GROUP_NAME, type: 'select', proxies: withNodes(cfg.final === 'direct' ? ['DIRECT', PROXY_GROUP_NAME] : [PROXY_GROUP_NAME, 'DIRECT']), ...flatNodes },
    ...regionGroups.flatMap((r) => {
      const m = members(r.names, providers, `(?i)${r.def.pattern}`);
      const manual = { name: r.def.name, type: 'select', ...m };
      const auto = { name: both ? regionAutoName(r.def) : r.def.name, type: 'url-test', ...urlTest, ...m };
      return both ? [manual, auto] : [cfg.regionType === 'select' ? manual : auto];
    }),
    ...airportGroups.map((a) => ({ name: a.name, type: 'select', ...members(a.names, a.use) })),
  ];

  // ---- 规则:自定义 → 所有域名类规则 → 所有 IP 类规则 → MATCH,避免 IP 规则提前触发 DNS 解析 ----
  const ruleProviders: Record<string, unknown> = {};
  const addProvider = (ref: RuleSetRef) => {
    const name = providerName(ref);
    ruleProviders[name] ??= {
      type: 'http',
      behavior: ref.type === 'geosite' ? 'domain' : 'ipcidr',
      format: 'mrs',
      interval: 86400,
      url: ruleSetUrl(ref, cfg.mirror, opts.origin, opts.rulesKey),
      // 新版 mihomo 下载规则也会走分流;jsDelivr 国内可直连,显式指定避免依赖节点可用
      proxy: cfg.mirror === 'github' ? PROXY_GROUP_NAME : 'DIRECT',
    };
    return name;
  };

  const ruleTarget = (item: RuleItem): string => {
    const t = targetOf(cfg, item);
    if (t === 'own') return ownGroupName(item);
    if (t === 'cat') return item.group === 'proxy' ? PROXY_GROUP_NAME : groupDef(item.group).name;
    // 指向的地区/机场组不存在(没有匹配节点)时退回 🚀 节点选择
    return outName(t) ?? PROXY_GROUP_NAME;
  };

  // 严格按用户排好的顺序输出;服务类 IP 规则带 no-resolve,放在任何位置都不会触发 DNS 解析
  const rules: string[] = [];
  for (const id of effectiveOrder(cfg)) {
    const custom = CUSTOM_BY_ID.get(id);
    if (custom) {
      for (const d of customDomains(cfg, custom.kind)) rules.push(`DOMAIN-SUFFIX,${d},${custom.target}`);
      continue;
    }
    const item = ITEM_BY_ID.get(id)!;
    const target = ruleTarget(item);
    for (const ref of item.sets) {
      const name = addProvider(ref);
      rules.push(`RULE-SET,${name},${target}${ref.type === 'geoip' && !ref.resolve ? ',no-resolve' : ''}`);
    }
  }
  rules.push(`MATCH,${FINAL_GROUP_NAME}`);

  const out: Record<string, unknown> = {
    'mixed-port': 7890,
    'allow-lan': cfg.allowLan,
    'bind-address': '*',
    mode: 'rule',
    'log-level': 'info',
    ipv6: cfg.ipv6,
    'unified-delay': true,
    'tcp-concurrent': true,
    'find-process-mode': 'strict',
    'external-controller': '127.0.0.1:9090',
    profile: { 'store-selected': true, 'store-fake-ip': true },
  };

  const dns = buildDns(cfg, addProvider);
  if (dns) out.dns = dns;

  if (cfg.sniffer) {
    out.sniffer = {
      enable: true,
      'force-dns-mapping': true,
      'parse-pure-ip': true,
      'override-destination': false,
      sniff: {
        HTTP: { ports: [80, '8080-8880'], 'override-destination': true },
        TLS: { ports: [443, 8443] },
        QUIC: { ports: [443, 8443] },
      },
      'skip-domain': ['Mijia Cloud', '+.push.apple.com'],
    };
  }

  if (proxies.length) out.proxies = proxies;
  if (providers.length) out['proxy-providers'] = proxyProviders;
  out['proxy-groups'] = proxyGroups;
  out['rule-providers'] = ruleProviders;
  out.rules = rules;
  return out;
}

function buildDns(cfg: UserConfig, addProvider: (ref: RuleSetRef) => string): Record<string, unknown> | undefined {
  if (cfg.dns === 'none') return undefined;
  const cnDoh = ['https://doh.pub/dns-query', 'https://dns.alidns.com/dns-query'];
  const dns: Record<string, unknown> = {
    enable: true,
    ipv6: cfg.ipv6,
    'respect-rules': false,
    'default-nameserver': ['223.5.5.5', '119.29.29.29'],
    nameserver: cnDoh,
    'proxy-server-nameserver': cnDoh,
  };
  if (cfg.dns === 'redir-host') {
    dns['enhanced-mode'] = 'redir-host';
    return dns;
  }
  dns['enhanced-mode'] = 'fake-ip';
  dns['fake-ip-range'] = '198.18.0.1/16';
  dns['fake-ip-filter-mode'] = 'blacklist';
  dns['fake-ip-filter'] = [
    '*.lan',
    '*.local',
    '*.localdomain',
    '+.stun.*.*',
    '+.stun.*.*.*',
    'localhost.ptlogin2.qq.com',
    'dns.msftncsi.com',
    '+.msftconnecttest.com',
    '+.msftncsi.com',
    'time.*.com',
    'ntp.*.com',
    '+.market.xiaomi.com',
  ];
  if (cfg.dns === 'fakeip-safe') {
    // 海外域名交给代理后的 DoH 解析,防止 DNS 泄露
    const name = addProvider({ type: 'geosite', name: 'geolocation-!cn' });
    dns['nameserver-policy'] = {
      [`rule-set:${name}`]: [
        `https://1.1.1.1/dns-query#${PROXY_GROUP_NAME}`,
        `https://8.8.8.8/dns-query#${PROXY_GROUP_NAME}`,
      ],
    };
  }
  return dns;
}

export function toYaml(obj: Record<string, unknown>): string {
  return stringify(obj, { lineWidth: 0 });
}
